import React, { useState, useEffect } from "react";
import StudentProgress from "../../../Api/StudentProgress";
import { useNavigate } from "react-router-dom";
import CourseService from "../../../Api/CourseService";

const EnrolledCourse = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [downloadLoading, setDownloadLoading] = useState({});
    const [downloadError, setDownloadError] = useState({});

    useEffect(() => {
        console.log("Component mounted, fetching student progress...");
        fetchStudentProgress();
    }, []);

    const fetchStudentProgress = async () => {
        try {
            console.log("Calling GetStudentProgress API...");
            const studentProgress = new StudentProgress();
            const response = await studentProgress.GetStudentProgress();
            console.log("API Response:", response);
            
            if (response?.data?.status === "success") {
                setCourses(response.data.data);
            } else {
                console.error("API response format unexpected:", response);
                setError("Failed to load courses data");
            }
        } catch (error) {
            console.error("Error in fetchStudentProgress:", error);
            setError(error.message || "Failed to fetch courses");
        } finally {
            setLoading(false);
        }
    };

    const handleCourseClick = (courseId) => {
        navigate('/course-study', { state: { courseId } });
    };

    const handleDownloadDocument = async (courseId) => {
        setDownloadLoading(prev => ({ ...prev, [courseId]: true }));
        setDownloadError(prev => ({ ...prev, [courseId]: null }));
        try {
            const courseService = new CourseService();
            const response = await courseService.GetCourseFilesbyID(courseId);
            if (response?.data?.status === "success" && response.data.data?.files?.length > 0) {
                // Download each file one by one
                for (const file of response.data.data.files) {
                    // Use file_url if available, otherwise fallback
                    let downloadUrl = file.file_url || `/download?file_id=${file.file_id || file.id}`;
                    window.open(downloadUrl, '_blank');
                }
            } else {
                setDownloadError(prev => ({ ...prev, [courseId]: "No files available for this course." }));
            }
        } catch (err) {
            setDownloadError(prev => ({ ...prev, [courseId]: "Failed to download files." }));
        } finally {
            setDownloadLoading(prev => ({ ...prev, [courseId]: false }));
        }
    };

    if (loading) {
        return <div className="text-center">Loading...</div>;
    }

    if (error) {
        return <div className="text-center text-danger">Error: {error}</div>;
    }

    const enrolledCourses = courses;
    const activeCourses = courses.filter(course => !course.is_completed);
    const completedCourses = courses.filter(course => course.is_completed);

    const renderCourseCard = (course) => (
        <div className="col-lg-6 col-md-6 col-12" key={course.course_id}>
            <div className="rbt-card variation-01 rbt-hover">
                <div className="rbt-card-img">
                    <a onClick={() => handleCourseClick(course.course_id)} style={{ cursor: 'pointer' }}>
                        <img
                            src={course.course_thumbnail}
                            alt={course.course_title}
                        />
                    </a>
                </div>
                <div className="rbt-card-body">
                    <div className="rbt-card-top">
                        <div className="rbt-review">
                            <div className="rating">
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                            </div>
                        </div>
                    </div>
                    <h4 className="rbt-card-title">
                        <a onClick={() => handleCourseClick(course.course_id)} style={{ cursor: 'pointer' }}>
                            {course.course_title}
                        </a>
                    </h4>
                    <ul className="rbt-meta">
                        <li>
                            <i className="feather-book" />
                            {course.total_videos} Lessons
                        </li>
                        <li>
                            <i className="feather-check-circle" />
                            {course.completed_videos} Completed
                        </li>
                    </ul>
                    <div className="rbt-progress-style-1 mb--20 mt--10">
                        <div className="single-progress">
                            <h6 className="rbt-title-style-2 mb--10">Complete</h6>
                            <div className="progress">
                                <div
                                    className="progress-bar wow fadeInLeft bar-color-success"
                                    data-wow-duration="0.5s"
                                    data-wow-delay=".3s"
                                    role="progressbar"
                                    style={{ width: `${course.progress_percentage}%` }}
                                    aria-valuenow={course.progress_percentage}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                ></div>
                                <span className="rbt-title-style-2 progress-number">
                                    {course.progress_percentage}%
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="rbt-card-bottom">
                        {course.is_completed ? (
                            <>
                                <button
                                    className="rbt-btn btn-sm bg-primary-opacity w-100 text-center"
                                    onClick={() => handleDownloadDocument(course.course_id)}
                                    disabled={downloadLoading[course.course_id]}
                                >
                                    {downloadLoading[course.course_id] ? 'Downloading...' : 'Download Document'}
                                </button>
                                {downloadError[course.course_id] && (
                                    <div className="text-danger text-center mt-2" style={{ fontSize: '0.9rem' }}>
                                        {downloadError[course.course_id]}
                                    </div>
                                )}
                            </>
                        ) : (
                            <a
                                className="rbt-btn btn-sm bg-primary-opacity w-100 text-center"
                                onClick={() => handleCourseClick(course.course_id)}
                                style={{ cursor: 'pointer' }}
                            >
                                Continue Learning
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="content">
                <div className="section-title">
                    <h4 className="rbt-title-style-3">Enrolled Courses</h4>
                </div>
                <div className="advance-tab-button mb--30">
                    <ul
                        className="nav nav-tabs tab-button-style-2 justify-content-start"
                        id="myTab-4"
                        role="tablist"
                    >
                        <li role="presentation">
                            <a
                                href="#"
                                className="tab-button active"
                                id="home-tab-4"
                                data-bs-toggle="tab"
                                data-bs-target="#home-4"
                                role="tab"
                                aria-controls="home-4"
                                aria-selected="true"
                            >
                                <span className="title">Enrolled Courses ({enrolledCourses.length})</span>
                            </a>
                        </li>
                        <li role="presentation">
                            <a
                                href="#"
                                className="tab-button"
                                id="profile-tab-4"
                                data-bs-toggle="tab"
                                data-bs-target="#profile-4"
                                role="tab"
                                aria-controls="profile-4"
                                aria-selected="false"
                            >
                                <span className="title">Active Courses ({activeCourses.length})</span>
                            </a>
                        </li>
                        <li role="presentation">
                            <a
                                href="#"
                                className="tab-button"
                                id="contact-tab-4"
                                data-bs-toggle="tab"
                                data-bs-target="#contact-4"
                                role="tab"
                                aria-controls="contact-4"
                                aria-selected="false"
                            >
                                <span className="title">Completed Courses ({completedCourses.length})</span>
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="tab-content">
                    <div
                        className="tab-pane fade active show"
                        id="home-4"
                        role="tabpanel"
                        aria-labelledby="home-tab-4"
                    >
                        <div className="row g-5">
                            {enrolledCourses.map(renderCourseCard)}
                        </div>
                    </div>
                    <div
                        className="tab-pane fade"
                        id="profile-4"
                        role="tabpanel"
                        aria-labelledby="profile-tab-4"
                    >
                        <div className="row g-5">
                            {activeCourses.map(renderCourseCard)}
                        </div>
                    </div>
                    <div
                        className="tab-pane fade"
                        id="contact-4"
                        role="tabpanel"
                        aria-labelledby="contact-tab-4"
                    >
                        <div className="row g-5">
                            {completedCourses.map(renderCourseCard)}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EnrolledCourse;
